import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import * as LucideIcons from "lucide-react";

const Header: React.FC = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('');

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 10);

            // Update active section based on scroll position
            const sections = ['solution', 'success-stories', 'security', 'demo'];
            const currentSection = sections?.find(section => {
                const element = document.getElementById(section);
                if (element) {
                    const rect = element?.getBoundingClientRect();
                    return rect?.top <= 100 && rect?.bottom >= 100;
                }
                return false;
            });

            if (currentSection) {
                setActiveSection(currentSection);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const headerHeight = 80;
            const elementPosition = element?.getBoundingClientRect()?.top;
            const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
        setIsMobileMenuOpen(false);
    };

    const handleDemoRequest = () => {
        scrollToSection('demo');
    };

    const navigationItems = [
        { id: 'solution', label: 'Solution Overview', icon: 'Monitor' },
        { id: 'success-stories', label: 'Success Stories', icon: 'TrendingUp' },
        { id: 'security', label: 'Security & Compliance', icon: 'Shield' },
    ];

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-100 transition-all duration-200 ${
                isScrolled ? 'nav-blur shadow-nav' : 'bg-white'
            }`}
        >
            <div className="max-w-9xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-20">
                    {/* Logo and Trust Badge */}
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-3">
                            <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
                                <Icon name="Building2" size={24} color="white" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-xl font-bold text-foreground">Civicrate</span>
                                <span className="text-xs text-muted-foreground">Digital Revenue Platform</span>
                            </div>
                        </div>

                        {/* Government Integration Badge */}
                        <div className="hidden sm:flex items-center space-x-2 px-3 py-1 bg-muted rounded-full">
                            <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                            <span className="text-sm font-medium text-success">Ghana.gov Integrated</span>
                        </div>
                    </div>

                    {/* Desktop Navigation */}
                    <nav className="hidden lg:flex items-center space-x-8">
                        {navigationItems?.map((item) => (
                            <button
                                key={item?.id}
                                onClick={() => scrollToSection(item?.id)}
                                className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                                    activeSection === item?.id
                                        ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                                }`}
                            >
                                <Icon name={item?.icon as keyof typeof LucideIcons} size={16} />
                                <span>{item?.label}</span>
                            </button>
                        ))}
                    </nav>

                    {/* CTA and Mobile Menu */}
                    <div className="flex items-center space-x-4">
                        <Button
                            variant="default"
                            onClick={handleDemoRequest}
                            iconName="Calendar"
                            iconPosition="left"
                            className="hidden sm:flex"
                        >
                            Request Demo
                        </Button>

                        <Button
                            variant="outline"
                            onClick={handleDemoRequest}
                            iconName="Calendar"
                            size="icon"
                            className="sm:hidden"
                        />

                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="lg:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-200"
                        >
                            <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={24} />
                        </button>
                    </div>
                </div>

                {/* Mobile Menu */}
                {isMobileMenuOpen && (
                    <div className="lg:hidden absolute top-full left-0 right-0 bg-white border-t border-border shadow-card animate-slide-down">
                        <div className="px-4 py-4 space-y-2">
                            {navigationItems?.map((item) => (
                                <button
                                    key={item?.id}
                                    onClick={() => scrollToSection(item?.id)}
                                    className={`flex items-center space-x-3 w-full px-4 py-3 rounded-lg text-left transition-colors duration-200 ${
                                        activeSection === item?.id
                                            ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                                    }`}
                                >
                                    <Icon name={item?.icon as keyof typeof LucideIcons} size={20} />
                                    <span className="font-medium">{item?.label}</span>
                                </button>
                            ))}

                            <div className="pt-4 border-t border-border">
                                <Button
                                    variant="default"
                                    onClick={handleDemoRequest}
                                    iconName="Calendar"
                                    iconPosition="left"
                                    fullWidth
                                >
                                    Request Demo
                                </Button>
                            </div>

                            {/* Mobile Trust Badge */}
                            <div className="flex items-center justify-center space-x-2 px-3 py-2 bg-muted rounded-lg">
                                <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                                <span className="text-sm font-medium text-success">Ghana.gov Integrated</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {/* Progress Indicator */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-border">
                <div
                    className="h-full bg-primary transition-all duration-300 ease-out"
                    style={{
                        width: `${Math.min(100, (window.pageYOffset / (document.documentElement?.scrollHeight - window.innerHeight)) * 100)}%`
                    }}
                />
            </div>
        </header>
    );
};

export default Header;